import { Moment, tz as momentTz } from 'moment-timezone';

const guessedTimeZone = momentTz.guess();
// Always use the browser's detected timezone (removed Carbon restriction)
export const userTimeZone = guessedTimeZone;

export const tzMoment = (date?: string | Date | null | number, timeZone = userTimeZone): Moment => {
    // If no date provided or empty string, return current time in the specified timezone
    if (date === null || date === undefined || date === '') {
        return momentTz(undefined, timeZone);
    }

    // Handle numeric timestamps (including 0)
    if (typeof date === 'number') {
        return momentTz(new Date(date), timeZone);
    }

    // Handle string and Date objects
    return momentTz(date, timeZone);
};

/**
 * Date pickers work in calendar days. MUI's calendar hands back the picked day as local midnight,
 * and a stored `YYYY-MM-DD` answer is that same day for every guest, whatever their time zone.
 * Converting either through another zone moves the day: picking Oct 1 from London for a New York
 * event used to save Sep 30, because London's midnight is still Sep 30 in New York.
 */
export const calendarDayOf = (date: Date): string => tzMoment(date).format('YYYY-MM-DD');

/** A stored `YYYY-MM-DD` as local midnight, the shape MUI's calendar shows. */
export const dateOfCalendarDay = (day: string): Date => tzMoment(day).toDate();

/** The picked calendar day as a moment in `timeZone` (same day, that zone's midnight), for rules like "after check-in". */
export const calendarDayIn = (date: Date, timeZone: string): Moment => tzMoment(calendarDayOf(date), timeZone);

export const userFormattedTimestamp = (isoTimestamp: string, addOnOrAt?: boolean) => {
    const inUserTz = tzMoment(isoTimestamp).tz(userTimeZone);
    const now = tzMoment('', userTimeZone);

    if (inUserTz.isSame(now, 'date')) {
        return {
            preposition: addOnOrAt ? 'at' : '',
            formattedTime: inUserTz.format('h:mm a z'),
        };
    }

    const preposition = addOnOrAt ? 'on' : '';
    const format = inUserTz.isSame(now, 'year') ? 'MMM D, h:mm a z' : 'MMM D, YYYY h:mm a z';

    return {
        preposition,
        formattedTime: inUserTz.format(format),
    };
};

export type CeilTo30MinutesOptions = {
    /** Same-day ceiling as clock time (e.g. "23:30" or "23:30:00"). Omit for no cap. */
    capAt?: string;
};

const parseCapAtMinutes = (capAt: string): number | undefined => {
    const [hoursPart, minutesPart] = capAt.trim().split(':');
    const hours = Number(hoursPart);
    const minutes = Number(minutesPart);

    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours < 0 || minutes < 0 || minutes >= 60) {
        return undefined;
    }

    return hours * 60 + minutes;
};

export function ceilTo30Minutes(momentObj: Moment, options?: CeilTo30MinutesOptions) {
    const totalMinutes = momentObj.hours() * 60 + momentObj.minutes();
    let roundedMinutes = Math.ceil(totalMinutes / 30) * 30;

    if (options?.capAt) {
        const capMinutes = parseCapAtMinutes(options.capAt);
        if (capMinutes != null) {
            roundedMinutes = Math.min(roundedMinutes, capMinutes);
        }
    }

    return momentObj.clone().startOf('day').add(roundedMinutes, 'minutes');
}

export const formatMessageTimeStamp = (sentAt: string) => {
    const lastSeen = momentTz(sentAt, 'UTC');
    const now = momentTz();

    if (now.diff(lastSeen, 'days') < 1) {
        return lastSeen.fromNow();
    }

    if (lastSeen.month() === now.month() && lastSeen.year() === now.year()) {
        return lastSeen.format('MMMM Do');
    }

    return lastSeen.format('MMMM Do, YYYY');
};

/**
 * Get timezone name in the specified format
 * @param date - The date to get the timezone name for
 * @param timeZone - The timezone to get the name for (defaults to user's timezone)
 * @param format - The format of the timezone name: 'long' (e.g., "Pacific Standard Time") or 'short' (e.g., "PST")
 * @returns The timezone name in the specified format, or empty string if invalid
 */
export const getTimeZoneName = (
    date: string | Date | number,
    timeZone = userTimeZone,
    format: 'long' | 'short' = 'long'
): string => {
    try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '';

        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: format,
        });
        const parts = formatter.formatToParts(dateObj);
        return parts.find(part => part.type === 'timeZoneName')?.value || '';
    } catch {
        return '';
    }
};
